import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  IconButton,
  Typography,
  TextField,
  CircularProgress,
} from "@mui/material";
import { Save as SaveIcon, Close as CancelIcon } from "@mui/icons-material";

interface NotesCellProps {
  emailId: string;
  initialNotes?: string;
  onSaveNotes: (emailId: string, notes: string) => Promise<void>;
}

export function NotesCell({
  emailId,
  initialNotes = "",
  onSaveNotes,
}: NotesCellProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [notes, setNotes] = useState(initialNotes);
  const [editValue, setEditValue] = useState(initialNotes);
  const [saving, setSaving] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    setNotes(initialNotes);
    setEditValue(initialNotes);
  }, [initialNotes]);

  const handleEditStart = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent row click
    setIsEditing(true);
    setEditValue(notes);
  };

  const handleCancel = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsEditing(false);
    setEditValue(notes);
  };

  const handleSave = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      setSaving(true);
      await onSaveNotes(emailId, editValue);
      setNotes(editValue);
      setIsEditing(false);
    } catch (error) {
      console.error("Failed to save notes:", error);
      // Revert on error
      setEditValue(notes);
    } finally {
      setSaving(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      setIsEditing(false);
      setEditValue(notes);
    } else if (e.key === "Enter" && e.ctrlKey) {
      e.preventDefault();
      handleSave(e as unknown as React.MouseEvent);
    }
    e.stopPropagation();
  };

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent row click when editing
  };

  useEffect(() => {
    if (isEditing && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [isEditing]);

  if (isEditing) {
    return (
      <Box
        onClick={handleClick}
        sx={{
          position: "relative",
          width: "100%",
          minWidth: 150,
        }}
      >
        <TextField
          multiline
          rows={3}
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onClick={handleClick}
          inputRef={textareaRef}
          variant="outlined"
          size="small"
          placeholder="Add notes..."
          sx={{
            width: "100%",
            "& .MuiInputBase-root": {
              fontSize: "11px",
              fontFamily: "monospace",
              padding: "4px",
            },
            "& .MuiOutlinedInput-input": {
              padding: "4px",
            },
          }}
        />

        {/* Save/Cancel buttons overlay */}
        <Box
          sx={{
            position: "absolute",
            top: 2,
            right: 2,
            display: "flex",
            gap: 0.5,
            backgroundColor: "rgba(255, 255, 255, 0.9)",
            borderRadius: 1,
          }}
        >
          <IconButton
            size="small"
            onClick={handleSave}
            disabled={saving}
            sx={{ p: 0.5, minWidth: 20, height: 20 }}
          >
            {saving ? (
              <CircularProgress size={12} />
            ) : (
              <SaveIcon sx={{ fontSize: 14 }} color="primary" />
            )}
          </IconButton>
          <IconButton
            size="small"
            onClick={handleCancel}
            disabled={saving}
            sx={{ p: 0.5, minWidth: 20, height: 20 }}
          >
            <CancelIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      onClick={handleEditStart}
      sx={{
        width: "100%",
        minWidth: 100,
        maxWidth: 150,
        cursor: "pointer",
        "&:hover": {
          backgroundColor: "rgba(0, 0, 0, 0.04)",
        },
        borderRadius: 0.5,
        padding: "2px 4px",
      }}
    >
      <Typography
        variant="body2"
        sx={{
          fontSize: "11px",
          fontFamily: "monospace",
          whiteSpace: "pre-wrap",
          color: notes ? "text.primary" : "text.secondary",
          overflow: "hidden",
          textOverflow: "ellipsis",
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          lineHeight: 1.3,
        }}
      >
        {notes || "Click to add..."}
      </Typography>
    </Box>
  );
}
